import { cloneDeep } from "lodash";
import JsCookie from "js-cookie";

import * as PriceTools from "../PriceTools";
import { isBrowser } from "../common/utils";
import { CountryID, CountryCode, LanguageId, LanguageCode } from "../common/enums";
import { CtxType, LifecycleStatus, CountryInfo } from "../common/types";
import { countryMap } from "../common/constants";

import { I18nConf, GlobalInfo, I18nInfo, DetectI18nOptions } from "./types";
import { globalInfo, initGlobalInfo, setCookieOptions } from "./vars";
import * as utils from "./utils";

/**
 * 初始化 一些需要的信息方法
 * @param {GlobalInfo} info 用于获取一些所需信息
 */
export function initI18nNeedInfo(info: GlobalInfo): void {
  // 存储所需信息
  initGlobalInfo(info);
}

/**
 * framework中需要的函数
 * @param {CtxType} ctx framework工程中的ctx
 * @param {LifecycleStatus} lifecycleStatus framework工程中的lifecycleStatus
 * @param {I18nConf} initialI18nConf i18n初始信息
 * @returns {I18nInfo}
 */
export function resolveI18n(
  ctx: CtxType,
  lifecycleStatus: LifecycleStatus,
  initialI18nConf?: I18nConf
): I18nInfo {
  // 推导之前先获取cookieOptions设置
  if (globalInfo.hooks && globalInfo.hooks.onBeforeSetCookieOpts) {
    setCookieOptions(globalInfo.hooks.onBeforeSetCookieOpts(ctx));
  }

  // 调用推导i18n信息
  return detectI18nInfo({
    query: utils.getQuery(ctx.req) as { [key: string]: undefined | string },
    lifecycleStatus,
    // i18n信息
    initialI18nConf,
    // 获取cookie
    getCookie: (key: string) => utils.getValueFromCookie(ctx.req, key),
    // 设置cookie
    setCookie: (key: string, value: string | number) =>
      utils.setValueToCookie({ res: ctx.res, key, value }),
  });
}

/**
 * 推导i18n信息的方法
 * @param {DetectI18nOptions} options 推导所需要的参数
 * @returns {I18nInfo}
 */
export function detectI18nInfo(options: DetectI18nOptions): I18nInfo {
  // 推导获取countryCode
  const resolvedCountryCode = detectCountryCode(options);
  // 根据countryCode推导获取其他信息
  const normalizedI18n = normalizeI18n(options, resolvedCountryCode);

  // 推导完成后，根据设置看是否需要重置cookie值
  if (!globalInfo.closeInitSetCookie) {
    resetCookies(normalizedI18n, options);
  }

  // 推导完成时回调
  if (globalInfo.hooks && globalInfo.hooks.onCompleted) {
    globalInfo.hooks.onCompleted(normalizedI18n);
  }
  return normalizedI18n;
}

/**
 * 推导国家
 * @param {DetectI18nOptions} detectI18nOptions
 */
function detectCountryCode(detectI18nOptions: DetectI18nOptions): CountryCode | undefined {
  const { lifecycleStatus, initialI18nConf } = detectI18nOptions;
  // 服务端
  if (lifecycleStatus.isExport) {
    return undefined;
  }
  if (initialI18nConf && initialI18nConf.lang && initialI18nConf.locale) {
    // 使用服务端已推导出的值
    return initialI18nConf.locale.countryCode;
  }

  // 1. url 存在 /foo?countryCode=xx 时，使用 url 版本
  let queryCountryCode = detectI18nOptions.query.countryCode as string;
  // 转换成大写
  queryCountryCode = queryCountryCode ? queryCountryCode.toUpperCase() : queryCountryCode;
  if (queryCountryCode in CountryCode) {
    return queryCountryCode as CountryCode;
  }

  // 2. 使用 cookie 存储的上一次设置值
  let cookieCountryCode = detectI18nOptions.getCookie(globalInfo.COOKIE_KEYS_MAP.CountryCode);
  // 转换成大写
  cookieCountryCode = cookieCountryCode ? cookieCountryCode.toUpperCase() : cookieCountryCode;
  if (cookieCountryCode && cookieCountryCode in CountryCode) {
    return cookieCountryCode as CountryCode;
  }

  // 3. 均不存在时使用默认值
  return globalInfo.defaultCountryCode || CountryCode.ID;
}

/**
 * 推导语言
 * @param {DetectI18nOptions} detectI18nOptions
 * @param {CountryCode} countryCode
 */
function detectLanguageInfo(
  detectI18nOptions: DetectI18nOptions,
  countryCode: CountryCode
): {
  languageCode: LanguageCode;
  languageId: LanguageId;
} {
  // 语言与国家独立，推导过程是query -> cookie -> 工程设置的默认语言 -> 根据国家获取默认的语言Code
  let languageCode =
    detectI18nOptions.query.languageCode ||
    detectI18nOptions.getCookie(globalInfo.COOKIE_KEYS_MAP.LanguageCode);
  // 转换成小写
  languageCode = languageCode ? languageCode.toLowerCase() : languageCode;
  // 如果通过 query 和 cookie 推导不出来或者不正确，则使用工程设置的默认语言，不存在则设置为国家默认的默认语言
  const finalLanguageCode =
    languageCode && languageCode in LanguageCode
      ? (languageCode as LanguageCode)
      : globalInfo.defaultLanguageCode || countryMap[countryCode].languageCode;

  // 直接根据 语言Code 推导出 语言ID
  const languageId = utils.languageCode2Id(finalLanguageCode);
  return { languageCode: finalLanguageCode, languageId };
}

/**
 * 推导I18n信息
 */
function normalizeI18n(
  detectI18nOptions: DetectI18nOptions,
  countryCode: CountryCode | undefined
): I18nInfo {
  // 根据 countryCode 获取国家基本信息
  const info = countryCode ? cloneDeep(countryMap[countryCode]) : undefined;

  // 推导正确的语言信息
  if (info && countryCode) {
    const countryInfo = detectLanguageInfo(detectI18nOptions, countryCode);
    info.languageCode = countryInfo.languageCode;
    info.languageId = countryInfo.languageId;
  }

  // 根据推导的国家信息和语言信息创建i18n信息
  return createI18nInfo(info);
}

/**
 * 切换语言
 */
function switchLanguage({
  newLanguageCode,
  returnUrl,
  isReplace,
  customCompleteBehavior,
}: {
  newLanguageCode: LanguageCode;
  /**
   * 更新cookie后页面转向的目标页面
   */
  returnUrl?: string;
  /**
   * 更新cookie后页面转向页面是是否替换当前热url，不留下历史记录
   */
  isReplace?: boolean;
  /**
   * 自定义切换完成操作
   */
  customCompleteBehavior?: () => void;
}): void {
  // 语言码错误
  if (!(newLanguageCode in LanguageCode)) {
    throw new Error(`languageCode(${newLanguageCode}) not found`);
  }
  if (!isBrowser) {
    throw new Error("can not switch country on server side");
  } else {
    // 更新语言Code cookie
    JsCookie.set(
      globalInfo.COOKIE_KEYS_MAP.LanguageCode,
      newLanguageCode,
      globalInfo.cookieOptions
    );
    // 更新语言Id cookie
    JsCookie.set(
      globalInfo.COOKIE_KEYS_MAP.LanguageId,
      LanguageId[newLanguageCode].toString(),
      globalInfo.cookieOptions
    );
    // 有自定义切换完成操作的时候执行自定义函数
    if (customCompleteBehavior) {
      customCompleteBehavior();
      return;
    }
    // 更新完后重定向页面或者重新加载页面
    if (globalInfo.buildEnv === "local") {
      if (returnUrl) {
        location[isReplace ? "replace" : "assign"](returnUrl);
      } else {
        location.reload();
      }
    } else {
      // 重定向至目标域名
      const newUrl = returnUrl || location.href;
      location[isReplace ? "replace" : "assign"](newUrl);
    }
  }
}

/**
 * 切换国家
 */
function switchCountry({
  newCountryCode,
  returnUrl,
  isReplace,
  customCompleteBehavior,
}: {
  newCountryCode: CountryCode;
  /**
   * 更新cookie后页面转向的目标页面
   */
  returnUrl?: string;
  /**
   * 更新cookie后页面转向页面是是否替换当前热url，不留下历史记录
   */
  isReplace?: boolean;
  /**
   * 自定义切换完成操作
   */
  customCompleteBehavior?: () => void;
}): void {
  // 语言码错误
  if (!(newCountryCode in CountryCode)) {
    throw new Error(`countryCode(${newCountryCode}) not found`);
  }
  if (!isBrowser) {
    throw new Error("can not switch country on server side");
  } else {
    // 更新国家Code cookie
    JsCookie.set(globalInfo.COOKIE_KEYS_MAP.CountryCode, newCountryCode, globalInfo.cookieOptions);
    // 更新国家Code cookie
    JsCookie.set(
      globalInfo.COOKIE_KEYS_MAP.CountryId,
      CountryID[newCountryCode].toString(),
      globalInfo.cookieOptions
    );
    // 有自定义切换完成操作的时候执行自定义函数
    if (customCompleteBehavior) {
      customCompleteBehavior();
      return;
    }
    // 切换语言国家完成的默认操作
    if (globalInfo.buildEnv === "local") {
      if (returnUrl) {
        location[isReplace ? "replace" : "assign"](returnUrl);
      } else {
        location.reload();
      }
    } else {
      // 重定向至目标域名
      const newUrl = (returnUrl || location.href).replace(
        /(^https?:\/\/(?:\w+-)?m-)(\w+)(\.akulaku.com.*$)/,
        ($0, $1, $2, $3) => {
          // $1 https://test-m-
          // $2 id
          // $3 .akulaku.com/*
          return $1 + newCountryCode.toLowerCase() + $3;
        }
      );
      location[isReplace ? "replace" : "assign"](newUrl);
    }
  }
}

/**
 * 创建i18n信息
 */
export function createI18nInfo(info: CountryInfo | undefined): I18nInfo {
  return {
    lang: info ? info.languageCode : undefined,
    locale: {
      countryId: info ? info.countryId : undefined,
      countryCode: info ? info.countryCode : undefined,
      languageId: info ? info.languageId : undefined,
      languageCode: info ? info.languageCode : undefined,
      currencyCode: info ? info.currencyCode : undefined,
      countryCode2Id: utils.countryCode2Id,
      countryId2Code: utils.countryId2Code,
      languageCode2Id: utils.languageCode2Id,
      switchLanguage: info
        ? switchLanguage
        : (): void => {
            throw new Error(`uncertain i18n`);
          },
      switchCountry: info
        ? switchCountry
        : (): void => {
            throw new Error(`uncertain i18n`);
          },
    },
    /**
     * 金额格式化方法
     * @param {number | string} value 金额
     * @param {*} format 默认"shortprice" "shortprice"(缩写) | "price"(全显)
     * @param {*} needCurrencyCode 是否需要显示货币符号 默认 true
     */
    format: function (
      value: number | string,
      format: "price" | "shortprice",
      needCurrencyCode = true
    ): string | number | undefined {
      return info && info.countryCode
        ? PriceTools.formatPrice(info.countryCode, value, format, needCurrencyCode)
        : undefined;
    },
  };
}

/**
 * 重置cookie值
 */
export function resetCookies(normalizedI18n: I18nInfo, detectI18nOptions: DetectI18nOptions): void {
  if (normalizedI18n.lang) {
    // 检查 前后端共用的几个 i18n 字段，在 cookie 里的值是否正确
    [
      [globalInfo.COOKIE_KEYS_MAP.CountryCode, normalizedI18n.locale.countryCode],
      [globalInfo.COOKIE_KEYS_MAP.CountryId, normalizedI18n.locale.countryId],
      [globalInfo.COOKIE_KEYS_MAP.LanguageCode, normalizedI18n.locale.languageCode],
      [globalInfo.COOKIE_KEYS_MAP.LanguageId, normalizedI18n.locale.languageId],
    ].forEach((item) => {
      const field = item[0] as string;
      const resolvedValue = item[1];
      const cookieValue = detectI18nOptions.getCookie(field);
      if (cookieValue !== resolvedValue && resolvedValue) {
        detectI18nOptions.setCookie(field, resolvedValue);
      }
    });
  }
}
